import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, Monitor, Clock, Package, RefreshCw } from "lucide-react";

const ShippingPolicy = () => {
  return (
    <div className="min-h-screen" data-testid="shipping-policy-page">
      <Header />
      
      <section className="pt-24 pb-16 bg-gradient-to-b from-primary/10 to-background">
        <div className="container text-center">
          <h1 className="text-4xl md:text-5xl font-serif text-primary mb-4 font-bold">Shipping and Delivery Policy</h1>
          <p className="text-lg text-foreground/70 max-w-2xl mx-auto font-medium">
            Digital service delivery information
          </p>
        </div>
      </section>

      <section className="py-12 bg-background">
        <div className="container max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <div className="flex items-start gap-3 mb-4 p-4 bg-primary/5 rounded-lg">
              <Monitor className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-foreground/80 m-0">
                Siwon Galaxy / Siwon Harmony provides digital and online services only. This policy explains how our services are delivered to you.
              </p>
            </div>
            <div className="mb-8 p-4 bg-primary/5 border-l-4 border-primary rounded-r-lg">
              <p className="text-sm text-foreground/80 m-0">
                <span className="font-semibold text-foreground">Operated by:</span> Siwon Health Care Resources Pvt. Ltd.<br />
                <span className="font-semibold text-foreground">Registered Address:</span> H.No. 330-B, Mianwali Colony, Gurgaon - 122001, Haryana, India<br />
                <span className="font-semibold text-foreground">CIN:</span> U85110HR2003PTC069710 &nbsp;·&nbsp; <span className="font-semibold text-foreground">GSTIN:</span> 06AAHCS1570M1Z3
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">1. Nature of Service</h2>
                <p className="text-foreground/80 leading-relaxed">
                  We provide <strong>digital and online services only</strong>. No physical shipping is involved unless specifically mentioned on the website. Our services include:
                </p>
                <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4 mt-3">
                  <li>Online coaching and consulting sessions</li>
                  <li>Siwon Harmony App access and subscriptions</li>
                  <li>Membership programs with digital content</li>
                  <li>Webinars and online workshops</li>
                  <li>Digital assessments and reports (PDF)</li>
                  <li>Educational content and resources</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">2. Delivery Method</h2>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-muted/30 rounded-lg p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Mail className="w-5 h-5 text-primary" />
                      <span className="font-semibold">Email Delivery</span>
                    </div>
                    <p className="text-sm text-foreground/70">
                      Login credentials, session links, downloadable resources, and confirmations
                    </p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Monitor className="w-5 h-5 text-primary" />
                      <span className="font-semibold">Dashboard Access</span>
                    </div>
                    <p className="text-sm text-foreground/70">
                      App access, membership portal, course content, and digital tools
                    </p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-5 h-5 text-primary" />
                      <span className="font-semibold">Scheduled Sessions</span>
                    </div>
                    <p className="text-sm text-foreground/70">
                      Video call links for coaching sessions, webinars, and consultations
                    </p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Package className="w-5 h-5 text-primary" />
                      <span className="font-semibold">Instant Downloads</span>
                    </div>
                    <p className="text-sm text-foreground/70">
                      PDF reports, worksheets, and digital materials
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">3. Delivery Time</h2>
                <p className="text-foreground/80 leading-relaxed mb-4">
                  Access to digital services is typically provided as follows:
                </p>
                <div className="bg-muted/50 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-primary/10">
                      <tr>
                        <th className="text-left p-4 font-semibold">Service Type</th>
                        <th className="text-left p-4 font-semibold">Delivery Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      <tr>
                        <td className="p-4 text-foreground/80">App Subscription</td>
                        <td className="p-4 text-foreground/80">Instant access after payment</td>
                      </tr>
                      <tr>
                        <td className="p-4 text-foreground/80">Membership Programs</td>
                        <td className="p-4 text-foreground/80">Within 24 hours</td>
                      </tr>
                      <tr>
                        <td className="p-4 text-foreground/80">Coaching Sessions</td>
                        <td className="p-4 text-foreground/80">As per scheduled date/time</td>
                      </tr>
                      <tr>
                        <td className="p-4 text-foreground/80">Webinar Access</td>
                        <td className="p-4 text-foreground/80">Link sent before event</td>
                      </tr>
                      <tr>
                        <td className="p-4 text-foreground/80">Digital Resources</td>
                        <td className="p-4 text-foreground/80">Instant download available</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">4. Exchange Policy</h2>
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <RefreshCw className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-foreground/80 m-0">
                    Since our services are digital and often personalized, exchange is generally not applicable. If you wish to switch between service tiers or programs, please contact us to discuss available options.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">5. Non-Delivery Issues</h2>
                <p className="text-foreground/80 leading-relaxed mb-4">
                  If you do not receive access to your purchased service within the stated time frame, please:
                </p>
                <ol className="list-decimal list-inside text-foreground/80 space-y-2 ml-4 mb-4">
                  <li>Check your email inbox and spam/junk folder</li>
                  <li>Ensure you used the correct email address during purchase</li>
                  <li>Try logging in with your registered credentials</li>
                  <li>Contact our support team with your transaction details</li>
                </ol>
                <div className="bg-muted/50 rounded-lg p-6">
                  <p className="font-semibold text-foreground mb-3">Contact us for delivery issues:</p>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-primary" />
                    <a href="mailto:siwongalaxy108@gmail.com" className="text-primary hover:text-primary/80">
                      siwongalaxy108@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">6. Technical Requirements</h2>
                <p className="text-foreground/80 leading-relaxed">
                  To access our digital services, you will need a device with internet connectivity, a modern web browser, and a valid email address. For video sessions, a stable internet connection with audio/video capabilities is recommended.
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mt-12 pt-6 border-t border-border">
              Last updated: December 2025
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ShippingPolicy;
